// frontend/src/api/students.ts

import api from './axios';
import {
    Student,
    CreateStudentRequest,
    UpdateStudentRequest,
    EnrollmentInput
} from '../types/student';
import { AccessStatus, StatusLog } from '../types/student';

// Raw server shape: enrollments is an array of RawEnrollment
type RawEnrollment = {
    enrollmentId: string;
    course: {
        id: string;
        name: string;
        baseFee: number;
        periodMonths: number;
        maxViewerDiscount?: number;   // if your backend can send this
        isArchived: boolean;
    };
    discount: number;
    remaining: number;
    isFullyPaid: boolean;
    enrollDate: string;
    endDate: string;
    channelAccessStart: string | null;
    originalBaseFee: number;
    channelAccessEnd: string | null;
    graceUsed: boolean;
    gracePeriodDays: number;
    graceCount: number;
    status: AccessStatus;
    paidStatus: boolean;
    inviteLink?: string | null;
};

// RawStudent mirrors Student minus the id/enrollments mapping
type RawStudent = Omit<Student, 'id' | 'enrollments'> & {
    _id: string;
    id?: string;
    enrollments: RawEnrollment[];
    isTopper: boolean;
};

function normalize(s: RawStudent): Student {
    const { _id, id, enrollments, isTopper, ...rest } = s;
    return {
        ...rest,
        id: id ?? _id,
        isTopper,
        enrollments: enrollments.filter(e => e.course).map(e => ({
            id:                e.enrollmentId,
            course: {
                id:                e.course.id,
                name:              e.course.name,
                baseFee:           e.course.baseFee,
                periodMonths:      e.course.periodMonths,
                maxViewerDiscount: e.course.maxViewerDiscount,
                isArchived:        e.course.isArchived,
            },
            discount:          e.discount,
            remaining:         e.remaining,
            isFullyPaid:       e.isFullyPaid,
            enrollDate:        e.enrollDate,
            endDate:           e.endDate,
            channelAccessStart:e.channelAccessStart,
            channelAccessEnd:  e.channelAccessEnd,
            graceUsed:         e.graceUsed,
            gracePeriodDays:   e.gracePeriodDays,
            originalBaseFee:   e.originalBaseFee,
            graceCount:        e.graceCount,
            status:            e.status,
            paidStatus:        e.paidStatus,
            inviteLink:        e.inviteLink,
        })),
    };
}

export async function getStudents(): Promise<Student[]> {
    const res = await api.get<RawStudent[]>('/students');
    return res.data.map(normalize);
}

export async function getStudent(id: string): Promise<Student> {
    const res = await api.get<RawStudent>(`/students/${id}`);
    return normalize(res.data);
}

export async function createStudent(
    data: CreateStudentRequest | FormData
): Promise<Student> {
    const res = await api.post<RawStudent>('/students', data as any);
    return normalize(res.data);
}

export async function updateStudent(
    id: string,
    data: UpdateStudentRequest | FormData
): Promise<Student> {
    const res = await api.patch<RawStudent>(`/students/${id}`, data as any);
    return normalize(res.data);
}

export async function deleteStudent(id: string): Promise<void> {
    await api.delete(`/students/${id}`);
}

export async function grantGrace(
    id: string,
    enrollmentId: string,
    days: number
): Promise<Student> {
    const res = await api.patch<RawStudent>(
        `/students/${id}/enrollments/${enrollmentId}/grace`,
        { days }
    );
    return normalize(res.data);
}

export async function banEnrollment(
    id: string,
    enrollmentId: string
): Promise<Student> {
    const res = await api.patch<RawStudent>(`/students/${id}/enrollments/${enrollmentId}/ban`);
    return normalize(res.data);
}

export async function unbanEnrollment(
    id: string,
    enrollmentId: string
): Promise<Student> {
    const res = await api.patch<RawStudent>(`/students/${id}/enrollments/${enrollmentId}/unban`);
    return normalize(res.data);
}

export async function removeEnrollment(
    studentId: string,
    enrollmentId: string
): Promise<Student> {
    const res = await api.delete<RawStudent>(`/students/${studentId}/enrollments/${enrollmentId}`);
    return normalize(res.data);
}

export async function addEnrollment(
    studentId: string,
    enrollment: EnrollmentInput
): Promise<Student> {
    const res = await api.post<RawStudent>(
        `/students/${studentId}/enrollments`,
        enrollment
    );
    return normalize(res.data);
}

export async function revokeGrace(
    id: string,
    enrollmentId: string
): Promise<Student> {
    const res = await api.patch<RawStudent>(
        `/students/${id}/enrollments/${enrollmentId}/revoke-grace`
    );
    return normalize(res.data);
}

export async function getEnrollmentLogs(
    studentId: string,
    enrollmentId: string
): Promise<StatusLog[]> {
    const res = await api.get<StatusLog[]>(
        `/students/${studentId}/enrollments/${enrollmentId}/logs`
    );
    return res.data;
}

export async function markTopper(id: string): Promise<Student> {
    const res = await api.patch<RawStudent>(`/students/${id}/topper`);
    return normalize(res.data);
}

export async function unmarkTopper(id: string): Promise<Student> {
    const res = await api.patch<RawStudent>(`/students/${id}/topper/revoke`);
    return normalize(res.data);
}

export async function resendAccessEmail(
    studentId: string,
    enrollmentId: string
): Promise<{ success: boolean; message: string }> {
    const res = await api.post<{ success: boolean; message: string }>(
        `/students/${studentId}/enrollments/${enrollmentId}/resend-email`
    );
    return res.data;
}

export async function resendDriveAccess(
    studentId: string,
    enrollmentId: string
): Promise<{ success: boolean; message: string }> {
    const res = await api.post<{ success: boolean; message: string }>(
        `/students/${studentId}/enrollments/${enrollmentId}/resend-drive`
    );
    return res.data;
}
